import { useEffect, useState } from "react";
import { Menu, X, ShieldCheck } from "lucide-react";
import { NAV } from "@/constants/testIds";

const LINKS = [
  { id: "top", label: "Home", tid: "nav-link-home" },
  { id: "about", label: "About", tid: NAV.linkAbout },
  { id: "speakers", label: "Speakers", tid: NAV.linkSpeakers },
  { id: "agenda", label: "Agenda", tid: NAV.linkAgenda },
  { id: "partners", label: "Partners", tid: NAV.linkPartners },
  { id: "contact", label: "Contact", tid: NAV.linkContact },
];

const LOGO_URL =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_3da7d792-db89-448a-b5af-6ec6bdeb0313/artifacts/idk7f7k4_logo.png";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#0B1120]/90 backdrop-blur-xl border-b border-cyan-900/50"
          : "bg-[#0B1120]/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="#top"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <div className="h-10 w-10 rounded-md bg-white/95 p-1 ring-1 ring-cyan-500/30">
            <img
              src={LOGO_URL}
              alt="Convergence Experts"
              className="h-full w-full object-contain"
            />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="mono text-[10px] uppercase tracking-[0.24em] text-cyan-300/80">
              Organized by
            </span>
            <span className="font-display text-sm font-bold tracking-tight text-white">
              Convergence Experts
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={l.tid}
              className="px-3 py-2 text-sm text-slate-300 hover:text-cyan-300 transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-cyan-400 scale-x-0 group-hover:scale-x-100 origin-left transition-transform" />
            </a>
          ))}
          <a
            href="#contact"
            data-testid={NAV.ctaEnquire}
            className="ml-3 inline-flex items-center gap-2 rounded-full bg-cyan-400 text-slate-950 px-4 py-2 text-sm font-semibold hover:bg-cyan-300 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            Enquire
          </a>
        </nav>

        <button
          data-testid={NAV.mobileToggle}
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-cyan-500/30 text-cyan-200"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B1120] border-t border-cyan-900/50">
          <div className="px-6 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`${l.tid}-mobile`}
                onClick={() => setOpen(false)}
                className="py-2 text-slate-200 hover:text-cyan-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 text-slate-950 px-4 py-2 text-sm font-semibold"
            >
              <ShieldCheck className="w-4 h-4" /> Enquire
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
